import { createClient } from "@/utils/supabase/client";
import { AirlineData } from "./types";

export const fetchAirlines = async (
  supabaseClient: ReturnType<typeof createClient>,
  query: string,
  limit: number = 10
): Promise<AirlineData[]> => {
  if (!query.trim()) return [];

  const formattedQuery = query.replace(/'/g, "''").toLowerCase();
  let results: AirlineData[] = [];

  // First try exact IATA code match (matching fetchAirports pattern)
  const exactMatches = await supabaseClient
    .schema("nowadays")
    .from("airlines_database")
    .select("iata_code, name")
    .eq("iata_code", query.toUpperCase())
    .limit(limit);

  // Only return if we have data (ignore errors, continue to fuzzy search)
  if (exactMatches.data && exactMatches.data.length > 0) {
    return exactMatches.data;
  }

  // If there was an error on exact match, log it but continue to fuzzy search
  if (exactMatches.error) {
    console.error("Fetch airlines exact match error:", exactMatches.error);
  }

  // Then try fuzzy search on name
  const { data, error } = await supabaseClient
    .schema("nowadays")
    .from("airlines_database")
    .select("iata_code, name")
    .ilike("name", `%${formattedQuery}%`)
    .limit(limit);

  if (error) {
    console.error("Fetch airlines error:", error);
    return [];
  }

  if (data) {
    results = data;
  }

  // Sort results: exact name matches first, then by name
  results.sort((a, b) => {
    const aNameLower = a.name.toLowerCase();
    const bNameLower = b.name.toLowerCase();
    const queryLower = query.toLowerCase();

    // Exact name match first
    if (aNameLower === queryLower) return -1;
    if (bNameLower === queryLower) return 1;

    // Starts with query
    if (aNameLower.startsWith(queryLower) && !bNameLower.startsWith(queryLower)) return -1;
    if (!aNameLower.startsWith(queryLower) && bNameLower.startsWith(queryLower)) return 1;

    // Alphabetical
    return aNameLower.localeCompare(bNameLower);
  });

  return results.slice(0, limit);
};
